import type { TileListInitialSession } from '@tilelist/application/core/TileListInitialSession.ts';
import type { TileListPageLoadResult } from '@tilelist/application/core/TileListSession.ts';
import { replaceProduct } from '@tilelist/domain/composition/replaceProduct.ts';
import type { BrowserLifecycleFactory } from '../BrowserLifecycle.browser.ts';
import type { TileListBrowserWiring } from '../TileListBrowserWiring.browser.ts';
import { type MountedTileListWiring, type TileListWiring, mountWiring } from '../TileListWiringContext.svelte.ts';

type MountBrowserWiring = (
    wiring: TileListWiring,
    initialSession: TileListInitialSession,
    onReload: (result: TileListPageLoadResult, browserWiring: TileListBrowserWiring) => void | Promise<void>,
) => MountedTileListWiring;

type Dependencies = {
    mountBrowserWiring: MountBrowserWiring;
    scrollToTop(): void;
};

export function createTileListPageBrowserLifecycleFactory(dependencies: Dependencies): BrowserLifecycleFactory {
    return ({ initialSession, state, wiring, afterRender }) => {
        let active = true;
        let revision = 0;
        const mounted = dependencies.mountBrowserWiring(wiring, initialSession, async (result, browserWiring) => {
            if (!active) return;
            revision += 1;
            if (!result.page) {
                wiring.errorReporting.report({ error: new Error('No page data'), type: 'reload' });
                state.page = null;
                return;
            }
            state.page = result.page;
            await afterRender();
            if (!active) return;
            if (result.historic || result.shouldScroll) dependencies.scrollToTop();
            browserWiring.sendTracking(result.page, result.historic);
        });

        async function initialize(): Promise<void> {
            if (!state.page) return;
            const restoreRevision = revision;
            const initialPage = state.page;
            let restored = initialPage;
            try {
                restored = await mounted.browserWiring.maybeRestoreFromCache(initialPage);
            } catch (error) {
                wiring.errorReporting.report({ error, type: 'cache-restore' });
            }
            if (!active || restoreRevision !== revision) return;

            state.page = restored;
            state.cacheRestored = true;
            await afterRender();
            if (!active || restoreRevision !== revision) return;
            mounted.browserWiring.sendTracking(restored);
        }

        return {
            start() {
                void initialize().catch((error) => wiring.errorReporting.report({ error, type: 'initialization' }));
            },
            redirectToPdp() {
                mounted.browserWiring.saveScrollSelector();
                if (!state.page) return;
                void mounted.browserWiring
                    .savePageToCache(state.page)
                    .catch((error) => wiring.errorReporting.report({ error, type: 'cache-save' }));
            },
            updateProduct(item) {
                if (state.page) state.page = replaceProduct(state.page, item);
            },
            dispose() {
                active = false;
                mounted.cleanup();
            },
        };
    };
}

export const createTileListPageBrowserLifecycle = createTileListPageBrowserLifecycleFactory({
    mountBrowserWiring: mountWiring,
    scrollToTop: () => window.scrollTo({ top: 0, left: 0, behavior: 'instant' }),
});
